import { useMemo } from "react";

import { Icon_People, Icon_People_Slash } from "@shapr3d/frontend-lib";
import { Icon_Document_Filled24 } from "@shapr3d/frontend-lib";

import useTranslation from "../../../../../hooks/useTranslation";
import type { SharingSpace } from "../../../../../types";

import style from "./SpaceRow.module.css";

type SpaceRowProps =
  | {
      variant: "owner-private";
      space: Extract<SharingSpace, { spaceType: "private" }>;
      projectName?: string;
    }
  | {
      variant: "owner-team";
      space: Extract<SharingSpace, { spaceType: "team" }>;
      projectName?: string;
    }
  | {
      variant: "no-access";
      projectName: string;
    };

export const SpaceRow = (props: SpaceRowProps) => {
  const { t, m } = useTranslation();

  const { icon, label, description } = useMemo(() => {
    switch (props.variant) {
      case "owner-private": {
        return {
          icon: <Icon_People_Slash />,
          label: t(m.ShareProjectModal.SpaceAccessLabelPrivate),
          description: null,
        };
      }

      case "owner-team": {
        const { space } = props;
        const userCount = space.spaceUsers?.length ?? 0;
        const spaceName = space.spaceName;

        return {
          icon: <Icon_People />,
          label: spaceName
            ? t(m.ShareProjectModal.SpaceAccessLabelWithName, { spaceName })
            : t(m.ShareProjectModal.SpaceAccessLabelGeneric),
          description: t(m.ShareProjectModal.SpaceAccessDescription, { count: userCount }),
        };
      }

      case "no-access": {
        const { projectName } = props;
        return {
          icon: <Icon_Document_Filled24 />,
          label: t(m.ShareProjectModal.SpaceAccessLabelNoAccess, { projectName }),
          description: t(m.ShareProjectModal.SpaceAccessDescriptionNoAccess, { projectName }),
        };
      }
    }
  }, [props, m.ShareProjectModal, t]);

  return (
    <div className={style.accessSection} role="listitem">
      <div className={style.iconWrapper} aria-hidden="true">
        {icon}
      </div>
      <div className={style.spaceAccessWrapper}>
        <div className={style.spaceAccessLabel}>{label}</div>
        {description && <div className={style.spaceAccessDescription}>{description}</div>}
      </div>
      <div className={style.canAccessWrapper}>
        <div>{t(m.ShareAccess.CanAccess)}</div>
      </div>
    </div>
  );
};
